import { Fragment, useEffect, useRef, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { useQuery } from '@tanstack/react-query'
import footerMd from 'assets/images/donation/footer_img_884_360.png'
import footerLg from 'assets/images/donation/footer_img_1920_1200.png'
import mainMd from 'assets/images/donation/기부_main_img_884_360.png'
import mainLg from 'assets/images/donation/기부_main_img_1920_1200.png'
import CsrCardComponent from 'features/csr/components/CsrCardComponent'
import gsap from 'gsap'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { getCsrCert, getCsrDonation, getCsrKeywords } from 'shared/api/fetchs'
import SeoHelmet from 'shared/components/common/SeoHelmet'
import TopButton from 'shared/components/common/TopButton'
import STYLE_GUIDE from 'shared/constants/color'
import styled from 'styled-components'

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin)

const Donation = () => {
  const isMobile = useMediaQuery({ query: '(max-width:1199px)' })
  const [isMore, setIsMore] = useState([])

  const { data: keywordData } = useQuery({
    queryKey: ['csrKeywords', 0],
    queryFn: () => getCsrKeywords(0), // 0 = 기부
  })

  const { data: donationData } = useQuery({
    queryKey: ['csrDonation', 0],
    queryFn: () => getCsrDonation(),
  })

  const { data: imageData } = useQuery({
    queryKey: ['csrCert', 0],
    queryFn: () => getCsrCert(),
  })

  const keywords = keywordData?.Keyword || []
  const donations = donationData?.Donation || []
  const certs = imageData || []

  const handleMoreBtn = (idx) => setIsMore([...isMore, idx])

  /* SNAP SCROLL START */
  const headerRef = useRef(null)
  const contentsRef = useRef(null)
  const isScrolling = useRef(false)
  const touchStartY = useRef(0)

  useEffect(() => {
    const snapTo = (target) => {
      isScrolling.current = true
      document.body.style.overflow = 'hidden'
      gsap.to(window, {
        duration: 1,
        scrollTo: target,
        ease: 'power2.out',
        onComplete: () => {
          document.body.style.overflow = ''
          isScrolling.current = false
        },
      })
    }

    const trySnap = (deltaY) => {
      if (isScrolling.current || !headerRef.current || !contentsRef.current) return

      if (Math.ceil(window.scrollY) < contentsRef.current.offsetTop) {
        if (deltaY > 0) snapTo(contentsRef.current.offsetTop)
        if (deltaY < 0) snapTo(headerRef.current.offsetTop)
      }
    }

    // 마우스 휠
    const handleWheel = (e) => {
      if (isScrolling.current) {
        e.preventDefault()
        return
      }
      trySnap(e.deltaY)
    }

    // 터치 시작 (좌표 저장)
    const handleTouchStart = (e) => {
      touchStartY.current = e.touches[0].clientY
    }

    // 터치 종료 (방향 계산)
    const handleTouchEnd = (e) => {
      const endY = e.changedTouches[0].clientY
      const deltaY = touchStartY.current - endY
      trySnap(deltaY)
    }

    window.addEventListener('wheel', handleWheel, { passive: false })
    window.addEventListener('touchstart', handleTouchStart, { passive: true })
    window.addEventListener('touchend', handleTouchEnd, { passive: false })

    return () => {
      window.removeEventListener('wheel', handleWheel)
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [])
  /* SNAP SCROLL END */

  return (
    <>
      <SeoHelmet />
      <DonationWrap>
        <div
          className="header"
          ref={headerRef}
          style={isMobile ? {} : { backgroundImage: `url(${mainLg})` }}
        >
          <div className="header_text_container container">
            <h1 className="header_title">
              Warming Lives with Sharing
              <br />
              나눔으로 더하는 온기
            </h1>
            <div className="header_subtitle">
              데이터유니버스는 <b>‘나눔으로 더하는 온기 Warming Lives with Sharing’</b> 라는 비전
              아래
              {isMobile ? '' : <br />}
              사회 구성원 모두가 행복한 내일을 살아갈 수 있도록 따뜻한 나눔을 실천하고 있습니다.
            </div>
          </div>
          {isMobile && <img className="header_background_img" src={mainMd} alt="배경" />}
        </div>
        <div className="contents container" ref={contentsRef}>
          <div className="contents_keyword_container">
            <h2 className="contents_title">데이터유니버스가 {isMobile && <br />}나눈 따뜻함</h2>
            <div className="contents_description">
              데이터유니버스는 2023년부터{' '}
              <b>우리 사회의 지속 가능한 발전을 위해 소외 계층을 아우르는 폭넓은 지원</b>을 이어가고
              있습니다.
              <br />
              에너지, 의료, 아동, 노인 취약계층 및 이재민 등 따뜻한 관심이 필요한 이웃들에게
              실질적인 나눔을 통해 기업의 사회적 책임을 성실히 이행하고자 합니다.
            </div>
            <div className="keyword_chips_list">
              {keywords?.map((keyword, index) => (
                <div className="keyword_chip_item" key={index}>
                  {keyword}
                </div>
              ))}
            </div>
          </div>
          <div className="contents_card_list">
            {donations.map((item, index) => (
              <Fragment key={item?.Idx}>
                <CsrCardComponent
                  data={item}
                  isMoreOpen={isMore.includes(item?.Idx)}
                  handleMoreBtn={handleMoreBtn}
                />
                {!!donations?.[index + 1] && <div className="card_divider" />}
              </Fragment>
            ))}
            <div className="cert_container">
              <h3 className="cert_title">우수 기부 기업 인증 현황</h3>
              <div className="cert_list">
                {certs?.map((item, index) => (
                  <div className="cert_item" key={index}>
                    <img className="cert_img" src={item?.ImgUrl} alt={item?.Title} />
                    <div className="cert_text">{item?.Title}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div
          className="footer"
          style={{ backgroundImage: `url(${isMobile ? footerMd : footerLg})` }}
        >
          <span>
            <span style={{ color: STYLE_GUIDE.COLOR.main1 }}>데이터유니버스</span>
            {isMobile
              ? '는 우리 사회 구성원\n모두가 더 나은 세상에서 살아갈 수 있도록\n앞으로도 함께하겠습니다.'
              : '는 우리 사회 구성원 모두가\n더 나은 세상에서 살아갈 수 있도록 앞으로도 함께하겠습니다.'}
          </span>
        </div>
        <TopButton />
      </DonationWrap>
    </>
  )
}

export default Donation

const DonationWrap = styled.div`
  b {
    font-weight: 700;
  }

  .header {
    width: 100%;
    height: min(calc(100vw / 16 * 9), 1080px);
    background-size: auto min(calc(100vw * 535 / 1920), 535px);
    background-position: center bottom;
    background-repeat: no-repeat;

    @media screen and (min-width: 1920px) {
      background-size: 100% auto;
    }

    @media screen and (max-width: 1199px) {
      height: auto;
    }
  }

  .header_text_container {
    display: flex;
    flex-direction: column;
    gap: 30px;
    padding-top: 220px;

    @media screen and (max-width: 1199px) {
      padding-top: 120px;
    }
  }

  .header_title {
    color: ${STYLE_GUIDE.COLOR.black1};
    font-size: 56px;
    font-weight: 700;
    line-height: 72px;

    @media screen and (max-width: 1199px) {
      font-size: 36px;
      line-height: 48px;
    }
  }

  .header_subtitle {
    color: ${STYLE_GUIDE.COLOR.black1};
    font-size: 20px;
    font-weight: 400;
    line-height: 32px;

    @media screen and (max-width: 1199px) {
      font-size: 16px;
      line-height: 30px;
    }
  }

  .header_background_img {
    @media screen and (max-width: 1199px) {
      margin-top: 219px;
    }

    @media screen and (max-width: 883px) {
      margin-top: 156px;
    }
  }

  .contents {
    display: flex;
    flex-direction: column;
    gap: 120px;
    padding: 120px 0;

    @media screen and (max-width: 1199px) {
      gap: 80px;
      padding: 80px 0;
    }
  }

  .contents_keyword_container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 50px;
    font-size: 18px;
    font-weight: 400;
    line-height: 30px;
    text-align: center;

    @media screen and (max-width: 1199px) {
      align-items: flex-start;
      gap: 30px;
      text-align: left;
    }
  }

  .contents_title {
    color: ${STYLE_GUIDE.COLOR.black1};
    font-size: 36px;
    font-weight: 700;
    line-height: 48px;

    @media screen and (max-width: 1199px) {
      font-size: 24px;
      line-height: 36px;
    }
  }

  .contents_description {
    color: ${STYLE_GUIDE.COLOR.black1};
    font-size: 18px;
    font-weight: 400;
    line-height: 30px;

    @media screen and (max-width: 1199px) {
      font-size: 16px;
      line-height: 30px;
    }
  }

  .keyword_chips_list {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;

    @media screen and (max-width: 1199px) {
      gap: 12px;
    }
  }

  .keyword_chip_item {
    padding: 12px 20px;
    border: 1px solid ${STYLE_GUIDE.COLOR.gray1};
    border-radius: 100px;
    color: ${STYLE_GUIDE.COLOR.black3};
    font-size: 18px;
    font-weight: 400;
    line-height: 30px;

    @media screen and (max-width: 1199px) {
      padding: 8px 19.5px;
      font-size: 16px;
      line-height: 30px;
    }
  }

  .contents_card_list {
    display: flex;
    flex-direction: column;
    gap: 120px;

    @media screen and (max-width: 1199px) {
      gap: 80px;
    }
  }

  .card_divider {
    height: 0;
    border-top: 1px solid ${STYLE_GUIDE.COLOR.gray2};
  }

  .cert_container {
    display: flex;
    flex-direction: column;
    gap: 50px;

    @media screen and (max-width: 1199px) {
      gap: 20px;
    }
  }

  .cert_title {
    color: ${STYLE_GUIDE.COLOR.black1};
    font-size: 36px;
    font-weight: 700;
    line-height: 48px;

    @media screen and (max-width: 1199px) {
      font-size: 24px;
      line-height: 36px;
    }
  }

  .cert_list {
    display: flex;
    gap: 40px;

    @media screen and (max-width: 1199px) {
      flex-direction: column;
      gap: 30px;
    }
  }

  .cert_item {
    display: flex;
    flex-direction: column;
    gap: 20px;

    @media screen and (max-width: 1199px) {
      gap: 12px;
    }
  }

  .cert_text {
    color: ${STYLE_GUIDE.COLOR.black1};
    font-size: 22px;
    font-weight: 400;
    line-height: 34px;

    @media screen and (max-width: 1199px) {
      font-size: 16px;
      line-height: 30px;
    }
  }

  .footer {
    display: flex;
    justify-content: center;
    align-items: center;
    //margin-bottom: 100px;
    height: 846px;
    color: ${STYLE_GUIDE.COLOR.black3};
    font-size: 32px;
    font-weight: 400;
    line-height: 44px;
    text-align: center;
    background-size: 100% auto;
    background-position: center;
    background-repeat: no-repeat;
    white-space: pre-line;

    @media screen and (max-width: 1919px) {
      height: 528px;
    }

    @media screen and (max-width: 1199px) {
      //margin-bottom: 70px;
      height: 778px;
      font-size: 16px;
      line-height: 30px;
    }

    @media screen and (max-width: 883px) {
      height: 317px;
    }
  }
`
